/*
 * File: ProductSearchTab.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 14 Dec 2021 2:43:14 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Col, Row, Spin } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import NoResults from 'components/atoms/noResults/noResults';
import { ProductSelectionCard } from 'components/atoms/ProductSelectionCard';
import { SublineProductSelectionCard } from 'components/atoms/SublineProductSelectionCard';
import { WimsicalError } from 'components/atoms/WimsicalError';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { ProductsTableDataSource } from 'models/ProductsTableDataSource';
import { useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { setOffset } from 'redux/productsInfo/productsInfoSlice';
import { ReduxState } from 'redux/rootReducer';
import { useGetReleasedProductsQuery } from 'redux/services/useDonaldGrumpService';

interface Props {
  record?: ProductsTableDataSource;
  isLineLevel?: boolean;
  index?: number;
}

/* ****************** CSS ****************** */
const styles: InlineStylesModel = {
  loadingCard: {
    height: 159.4
  },
  infiniteScrollRow: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: 'calc((((100vh - 112px) - 46px) - 55px) - 191px)'
  },
  gridRow: {
    width: '100%',
    marginTop: '15px',
    height: '50vh'
  }
};

export const ProductSearchTab: React.FC<Props> = ({ record, isLineLevel, index }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { filterProductNumber, filterProductName, filterManufacturer, filterCategory, offset } = useSelector((state: ReduxState) => state.productsInfo);


  /* ****************** UseEffects ****************** */
  // Reset offset when filters change
  useEffect(() => {
    dispatch(setOffset(0));
  }, [filterProductNumber, filterProductName, filterManufacturer, filterCategory, dispatch]);

  /* ****************** Hook API ****************** */
  const {
    data: productsResponse,
    isLoading,
    isError,
    error,
    isFetching
  } = useGetReleasedProductsQuery({
    offset,
    dataAreaId: 'mdsi',
    searchName: filterProductNumber || undefined,
    productNumber: filterProductName || undefined,
    manufacturerId: filterManufacturer || undefined,
    productGroup: filterCategory || undefined
  });



  /* ****************** Functions ****************** */
  const handleNext = (): void => {
    dispatch(setOffset(offset + 25));
  };

  /* ****************** JSX / Variables ****************** */
  const productsJSX = productsResponse?.data.map((product) => (
    <Col key={product.itemNumber} md={24} lg={12} xxl={isLineLevel ? 12 : 24}>
      {isLineLevel ? <SublineProductSelectionCard index={index} record={record} product={product} /> : <ProductSelectionCard record={record} product={product} />}
    </Col>
  ));

  const hasMore = Boolean(productsResponse && productsResponse.totalCount > offset);

  const loadingJSX = useMemo(
    () => (
      <Row style={styles.gridRow} justify="center" align="middle">
        <Col span={24} xs={24} sm={12} md={8} lg={8} xl={8} xxl={6}>
          <BlockLoader direction="loader loader--slideUp" />
        </Col>
      </Row>
    ),
    []
  );

  if (isError) {
    const queryError = error as FetchBaseQueryError;

    console.log('ProductSearchTab API Error:', queryError);
    console.log('Error Status:', queryError.status);
    console.log('Error Data:', queryError.data);

    return (
      <Row justify="center" style={{ width: '100%' }}>
        <Col span={24}>
          <WimsicalError status={typeof queryError.status === 'number' ? queryError.status : 500} message={''} />
        </Col>
      </Row>
    );
  }

  const infoJSX = (
    <Row style={styles.gridRow} justify="center" align="middle">
      <NoResults errorMessage="No Products Were Found..." />
    </Row>
  );

  /* ****************** Render ****************** */
  if (isLoading || (isFetching && offset === 0)) {
    return loadingJSX;
  }

  if (!isLoading && productsResponse && !productsResponse.totalCount) {
    return infoJSX;
  }

  return (
    <div style={{ height: 650, overflowY: 'auto', overflowX: 'hidden' }} id="scrollable-target">
      <InfiniteScroll
        scrollableTarget="scrollable-target"
        dataLength={productsResponse?.data.length || 0}
        style={{ padding: '0px 20px' }}
        next={handleNext}
        hasMore={hasMore}
        initialScrollY={0}
        loader={
          <Row justify="center">
            <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning={isLoading || isFetching} />
          </Row>
        }>
        <Row style={{ marginTop: 8 }} gutter={[16, 16]}>
          {productsJSX}
        </Row>
      </InfiniteScroll>
    </div>
  );
};
